import { Stack } from "@mui/material";
import { differenceInDays, startOfDay } from "date-fns";
import { useParams } from "react-router-dom";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BankProductSection } from "web_myplann/Portfolio/Components/PortfolioTools/BankProductSection";
import { BondSection } from "web_myplann/Portfolio/Components/PortfolioTools/BondSection";
import { CommoditySection } from "web_myplann/Portfolio/Components/PortfolioTools/CommoditySection";
import { CryptoSection } from "web_myplann/Portfolio/Components/PortfolioTools/CryptoSection";
import { FundSection } from "web_myplann/Portfolio/Components/PortfolioTools/FundSection";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";

const sortByClosestToToday = <T extends Record<string, any>>(
	items: T[],
	dateProperty: keyof T,
): T[] => {
	return [...items].sort((a, b) => {
		const today = startOfDay(new Date());
		const aDate = a[dateProperty] ? new Date(a[dateProperty] as string) : null;
		const bDate = b[dateProperty] ? new Date(b[dateProperty] as string) : null;

		if (!aDate && !bDate) return 0;
		if (!aDate) return 1;
		if (!bDate) return -1;

		const aDiff = Math.abs(differenceInDays(today, aDate));
		const bDiff = Math.abs(differenceInDays(today, bDate));

		return aDiff - bDiff;
	});
};

export const ToolHistory = () => {
	const { id, index } = useParams();
	const { t } = useResource();

	const { data: portfolioDetail } = usePortfolioQuery(Number(id));

	const tool = portfolioDetail?.tools[Number(index)];

	const sortedInvestments = tool?.investments
		? sortByClosestToToday(tool.investments, "startAt")
		: [];

	const sortedBonds = tool?.bonds
		? sortByClosestToToday(tool.bonds, "issueDate")
		: [];

	const sortedCommodities = tool?.commodities
		? sortByClosestToToday(tool.commodities, "startAt")
		: [];

	const sortedCrypto = tool?.cryptocurrencyInstructions
		? sortByClosestToToday(tool.cryptocurrencyInstructions, "date")
		: [];

	const sortedBankProducts = tool?.bankProductInstructions
		? sortByClosestToToday(tool.bankProductInstructions, "startAt")
		: [];

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={t(Resources.PortfolioDetail.ToolHistory.title)}
					defaultRoute={DEFAULT_ROUTE}
				/>
			}
		>
			<Stack pt={2}>
				<FundSection investments={sortedInvestments} />
				<BondSection bonds={sortedBonds} />
				<CommoditySection commodities={sortedCommodities} />
				<CryptoSection crypto={sortedCrypto} />
				<BankProductSection bankProducts={sortedBankProducts} />
			</Stack>
		</MyPlannLayout>
	);
};
